import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { map, Observable, switchMap, timeout } from 'rxjs';
import {
  emptyTabBarSettings,
  parseTabBarConfig,
  TAB_BAR_PLATFORMS,
  TabBarConfig,
  TabBarPlatform,
  TabBarSettings,
  tabBarSettingKey
} from '../../models/tab-bar-settings.model';

interface SettingResponse {
  status: string;
  data: unknown;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class TabBarSettingsService {
  private readonly url: string;
  constructor(private readonly http: HttpClient, @Inject('API_URL') apiUrl: string, @Inject('API_VERSION') version: string) {
    this.url = `${apiUrl}/api/${version}/settings`;
  }

  read(platforms: readonly TabBarPlatform[] = TAB_BAR_PLATFORMS, strict = true): Observable<TabBarSettings> {
    const keys = platforms.map(tabBarSettingKey).join(',');
    return this.http.get<SettingResponse>(`${this.url}/filter`, { params: { keys } }).pipe(
      timeout({ first: 8000 }),
      map((response) => {
        if (response.status !== 'success' || !Array.isArray(response.data)) throw new Error('Could not load tab bar settings.');
        const result = emptyTabBarSettings();
        for (const platform of platforms) {
          const rows = response.data.filter((row) => row && typeof row === 'object' && row.key === tabBarSettingKey(platform));
          if (!rows.length) continue;
          try {
            if (rows.length !== 1 || rows[0].type !== 'json' || rows[0].is_public !== true) throw new Error('Invalid tab bar setting record.');
            result[platform] = parseTabBarConfig(rows[0].value);
          } catch (error) {
            if (strict) throw error;
            // A malformed device configuration must not break the storefront.
          }
        }
        return result;
      })
    );
  }

  save(platform: TabBarPlatform, value: TabBarConfig): Observable<TabBarConfig> {
    const config = parseTabBarConfig(value);
    const key = tabBarSettingKey(platform);
    // Re-read existence on retries: a preceding write may have succeeded even
    // if its acknowledgement/readback was interrupted.
    return this.read([platform]).pipe(
      switchMap((current) =>
        this.http
          .request<SettingResponse>(current[platform] ? 'PUT' : 'POST', current[platform] ? `${this.url}/${key}` : this.url, {
            body: { key, type: 'json', value: config, is_public: true }
          })
          .pipe(timeout({ first: 20000 }))
      ),
      map((response) => {
        const record = response.data as { key?: string; value?: unknown } | null;
        if (
          response.status !== 'success' ||
          !record ||
          record.key !== key ||
          JSON.stringify(parseTabBarConfig(record.value)) !== JSON.stringify(config)
        ) {
          throw new Error('The server did not confirm these settings. Please retry.');
        }
      }),
      switchMap(() => this.read([platform])),
      map((result) => {
        if (JSON.stringify(result[platform]) !== JSON.stringify(config)) throw new Error('Could not verify the saved settings. Please retry.');
        return result[platform] as TabBarConfig;
      })
    );
  }
}
